import { MAX_HORIZON_YEARS, MIN_HORIZON_YEARS } from "@shared/constants";
import { formatCount } from "@shared/roi/format";
import type { ChurnMode } from "@shared/roi/types";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { TextField } from "@client/components/Field";
import { Panel } from "@client/components/Panel";
import { CountField, PercentField } from "@client/components/editor/NumberFields";
import { Segmented } from "@client/components/editor/Segmented";
import { packageColorVar } from "@client/lib/packageColors";
import {
  minHorizonFor,
  mixHeadroom,
  unassignedIn,
  type DraftAction,
  type EditorModel,
  type EditorYear,
} from "@client/lib/scenarioDraft";

/**
 * Adoption and churn, one row per year.
 *
 * The split is clamped to the signings it is splitting — you cannot put 8
 * clients on Starter out of 5 signed — so the only way to be inconsistent is to
 * leave clients *unallocated*, which is a real modelling choice ("we will sign
 * them, we have not decided on what") and is shown as a chip on the row rather
 * than fixed behind the user's back. The engine reports the same thing as
 * `UNASSIGNED_CLIENTS`, and those clients earn nothing.
 */

const CHURN_OPTIONS = [
  { value: "COUNT" as const, label: "Clients" },
  { value: "RATE" as const, label: "% of base" },
];

interface Props {
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
  /** Inside the rail's dialog, which carries the title and the card chrome. */
  bare?: boolean;
}

export function AdoptionPanel({ model, dispatch, disabled, bare = false }: Props) {
  const floor = minHorizonFor(model);
  const atMax = model.years.length >= MAX_HORIZON_YEARS;
  const atFloor = model.years.length <= floor;

  // Which of the two rules is holding the last year in place. Worth saying,
  // because "Remove year" being dead is otherwise indistinguishable from broken.
  const floorReason =
    model.commitmentYears > MIN_HORIZON_YEARS
      ? `A ${model.commitmentYears}-year commitment needs a horizon at least that long.`
      : `A horizon runs for at least ${MIN_HORIZON_YEARS} years.`;

  return (
    <Panel
      bare={bare}
      title={bare ? undefined : "Adoption & churn"}
      description={
        bare ? undefined : "Who signs each year, which package they take, and who leaves."
      }
      // A year is the horizon, not a property of this panel — but this is the
      // panel where somebody notices they need another one, and sending them to
      // scenario settings to find a dropdown is a detour through a different
      // dialog. Same `horizon` action either way, so the two stay in step.
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled || atFloor}
            title={atFloor ? floorReason : `Drops year ${model.years.length} and its adoption.`}
            onClick={() => dispatch({ type: "horizon", years: model.years.length - 1 })}
          >
            Remove year
          </Button>
          <Button
            size="sm"
            disabled={disabled || atMax}
            title={atMax ? `A horizon runs to at most ${MAX_HORIZON_YEARS} years.` : undefined}
            onClick={() => dispatch({ type: "horizon", years: model.years.length + 1 })}
          >
            Add year
          </Button>
        </>
      }
    >
      <ul className="flex flex-col gap-3">
        {model.years.map((year, index) => (
          <YearRow
            key={index}
            year={year}
            index={index}
            model={model}
            dispatch={dispatch}
            disabled={disabled}
          />
        ))}
      </ul>
    </Panel>
  );
}

function YearRow({
  year,
  index,
  model,
  dispatch,
  disabled,
}: {
  year: EditorYear;
  index: number;
  model: EditorModel;
  dispatch: (action: DraftAction) => void;
  disabled: boolean;
}) {
  const unassigned = unassignedIn(year);
  const commitmentReached = index + 1 > model.commitmentYears;

  return (
    <li
      // The anchor a year warning links to — see `warningTarget`.
      id={`year-${index + 1}`}
      className="flex scroll-mt-4 flex-col gap-3 rounded-xl border border-line bg-chip-bg p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold uppercase tracking-wider text-ink">Year {index + 1}</h3>
        {unassigned > 0 && (
          <Badge kind="warning" title="These clients are not on any package, so they earn nothing.">
            {formatCount(unassigned)} unassigned
          </Badge>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <CountField
          label="New clients"
          value={year.newClients}
          disabled={disabled}
          hint="Total signings this year, before the split."
          onChange={(newClients) => dispatch({ type: "patchYear", index, patch: { newClients } })}
        />
      </div>

      {/*
        Churn gets its own row, with the figure in the first column and the unit
        beside it. The figure is the thing being read and compared down the
        column against "New clients" above it; "Churn measured in" only says
        which of two fields the one next to it is.

        `items-end` because the two controls are different heights — a label over
        an input, and a legend over a chip row — and lining them up on their
        bottom edges is what makes them read as one row rather than two.
      */}
      <div className="grid items-end gap-3 sm:grid-cols-2">
        {year.churnMode === "COUNT" ? (
          <CountField
            label="Clients leaving"
            value={year.churnCount}
            disabled={disabled}
            hint={
              commitmentReached
                ? undefined
                : `Nobody is past the ${model.commitmentYears}-year commitment yet, so this is held back.`
            }
            onChange={(churnCount) => dispatch({ type: "patchYear", index, patch: { churnCount } })}
          />
        ) : (
          <PercentField
            label="Churn rate"
            value={year.churnRatePct}
            disabled={disabled}
            hint="Of the clients who could leave this year."
            onChange={(churnRatePct) =>
              dispatch({ type: "patchYear", index, patch: { churnRatePct } })
            }
          />
        )}

        <Segmented
          legend="Churn measured in"
          value={year.churnMode}
          options={CHURN_OPTIONS}
          disabled={disabled}
          size="sm"
          onChange={(churnMode: ChurnMode) =>
            dispatch({ type: "patchYear", index, patch: { churnMode } })
          }
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold text-ink">Split across packages</span>
        <div className="grid gap-3 sm:grid-cols-3">
          {model.packages.map((pkg) => {
            const taken = year.mix[pkg.id] ?? 0;
            const headroom = mixHeadroom(year, pkg.id);
            return (
              <div key={pkg.id} className="flex items-end gap-2">
                <span
                  aria-hidden="true"
                  className="mb-2 inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: packageColorVar(pkg.colorIndex) }}
                />
                <div className="min-w-0 flex-1">
                  <CountField
                    label={pkg.name || "Unnamed package"}
                    value={taken}
                    max={headroom}
                    disabled={disabled}
                    hint={headroom === taken ? "At the limit for this year." : undefined}
                    onChange={(count) =>
                      dispatch({ type: "setMix", index, packageId: pkg.id, count })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TextField
        label="Note"
        placeholder="Why this year looks like this"
        maxLength={200}
        value={year.note ?? ""}
        disabled={disabled}
        onChange={(event) =>
          // An empty note is absent, not "": the schema trims and drops it, so
          // keeping "" here would make a cleared note look like a pending change
          // on every reload.
          dispatch({
            type: "patchYear",
            index,
            patch: { note: event.target.value === "" ? undefined : event.target.value },
          })
        }
      />
    </li>
  );
}
